import { useCallback, useEffect, useState } from 'react';
import { apiPost } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';

const STORAGE_KEY = 'kispro-sidebar-favorites';
const SAVED_AT_KEY = 'kispro-sidebar-favorites-saved-at';

function loadLocal(): string[] | null {
  try {
    const savedAt = parseInt(localStorage.getItem(SAVED_AT_KEY) || '0', 10);
    if (savedAt > 0 && Date.now() - savedAt < 120_000) {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (Array.isArray(stored)) return stored as string[];
    }
  } catch { /* ignore */ }
  return null;
}

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<string[]>(() => {
    const local = loadLocal();
    if (local) return local;
    return user.sidebar_favorites ?? [];
  });

  // Beim Mount localStorage mit Server abgleichen
  useEffect(() => {
    const local = loadLocal();
    if (!local && user.sidebar_favorites) {
      setFavorites(user.sidebar_favorites);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user.sidebar_favorites));
      } catch { /* ignore */ }
    }
  }, [user.sidebar_favorites]);

  const persist = useCallback((next: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      localStorage.setItem(SAVED_AT_KEY, String(Date.now()));
    } catch { /* ignore */ }
    void apiPost('/user/sidebar-favorites', { favorites: next }).catch(() => {
      /* nicht kritisch – localStorage übernimmt */
    });
  }, []);

  const toggle = useCallback((key: string) => {
    setFavorites((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      persist(next);
      return next;
    });
  }, [persist]);

  return { favorites, toggle, isFavorite: (k: string) => favorites.includes(k) };
}
